import React, { useState, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import Toolbar from '../../molecules/toolbar/Toolbar';
import Pagination from '../../molecules/pagination/Pagination';
import CategoryProductList from '../../organisms/categoryProductList';
import ShopNext from '@srcDS/components/molecules/shopNext';
import { Filter } from '../../molecules/filter/Filter';
import { Sort } from '../../molecules/filter/Sort';

import { useNostoContext } from '@srcDS/provider/nosto/NostoContext';
import { useCategoryContext } from './context/useCategoryContext';
import { useCategory } from '@srcDS/tastic/pages/category/hooks/useCategory';
import { getShopNextData } from '@srcDS/components/molecules/shopNext/ShopNext';

import styles from './Category.module.scss';

interface CategoryContentProps {
  filterDisplayValues: any;
  isMobile: boolean;
  setFilterDisplayValues: (values: any) => void;
  states: {
    viewMode: 'narrow' | 'wide';
    setViewMode: (mode: string) => void;
    hasProducts: boolean;
    hasFilters: boolean;
    numbOfTotalItems: number;
  };
  categoryTitle: string;
  listName: string;
  filtrableResult: boolean;
  currentPage: any;
}

const CategoryContent: React.FC<CategoryContentProps> = ({
  filterDisplayValues,
  setFilterDisplayValues,
  states,
  categoryTitle,
  listName,
  filtrableResult,
  currentPage,
  isMobile,
}) => {
  const { items, handlePageClick, stream, sortState, pageCount, productListRef } = useCategory() as any;

  const [sortShown, setSortShown] = useState(false);

  const {
    itemOffset,
    itemsLimit,
    filterCount,
    mergedFacets,
    localFilters,
    isGlobalLoaderShow,
    triggerRoute,
    filterTagsData,
    initialPage
  } = useCategoryContext() as any;

  const { nostoProductClick } = useNostoContext();

  const { projectSpecificData } = stream || {};

  const newOffset = itemOffset + itemsLimit;
  const page = Math.ceil(newOffset / itemsLimit);
  const hasNext = pageCount !== page;
  const hasProducts = items && items.length > 0;

  const handleNostoProductClick = useCallback(
    (productId: string, placement: string) => {
      const source = projectSpecificData?.merchandised_category;
      nostoProductClick(productId, placement, source);
    },
    [nostoProductClick, projectSpecificData],
  );

  const productList = useMemo(() => {
    if (filtrableResult) {
      return (
        <div className={styles.emptySearchTitle}>
          <h2>{`We can't seem to find what you're looking for ...`}</h2>
        </div>
      );
    }

    return (
      hasProducts && (
        <CategoryProductList
          listName={listName}
          categoryTitle={categoryTitle}
          gridType={states.viewMode}
          products={items}
          placement="Listing Page"
          customClasses={{
            root: styles.categoryListRoot,
          }}
          nostoPlacement={projectSpecificData?.merchandised_category_url ? 'categorypage-nosto-1' : undefined}
          nostoProductClick={handleNostoProductClick}
        />
      )
    );
  }, [sortState, stream, states, filtrableResult, listName, handleNostoProductClick]);

  const renderPagination = useMemo(() => {
    return (
      hasProducts &&
      pageCount > 1 && <Pagination pageCount={pageCount} initialPage={initialPage} handlePageClick={handlePageClick} />
    );
  }, [pageCount, initialPage, hasProducts, handlePageClick]);

  return (
    <>
      <div id="categoryPage" />
      <Toolbar
        {...states}
        numbersOfFilterApplied={filterCount}
        facets={mergedFacets}
        localFilters={localFilters}
        filterDisplayValues={filterDisplayValues}
        setFilterDisplayValues={setFilterDisplayValues}
        /*@ts-ignore*/
        sortState={sortState}
        /*@ts-ignore*/
        filterTagsData={filterTagsData}
        isMobile={isMobile}
        sortShown={sortShown}
        setSortShown={setSortShown}
        sortComponent={
          <Sort
            sortState={sortState}
            setFilterDisplayValues={setFilterDisplayValues}
            showTitle={false}
            showCloseButton={false}
          />
        }
      />

      {filterDisplayValues.filterShown && (
        <div className={styles.categoryAndFilter}>
          <Filter
            filters={mergedFacets}
            isGlobalLoaderShow={isGlobalLoaderShow}
            triggerRoute={triggerRoute}
            setFilterDisplayValues={setFilterDisplayValues}
          />
        </div>
      )}
      {filterDisplayValues.sortShown && <Sort sortState={sortState} setFilterDisplayValues={setFilterDisplayValues} />}
      <div className={clsx(styles.categoryContent)}>
        {isMobile && sortShown && (
          <>
            <hr />
            <div className={styles.mobileSortWrapper}>
              <Sort
                sortState={sortState}
                setFilterDisplayValues={setFilterDisplayValues}
                onClose={() => {
                  setSortShown(false);
                }}
              />
            </div>
          </>
        )}
        <div>
          <div>
            <div className={styles.categoryContentInner} ref={productListRef}>
              {productList}
            </div>
            <div className={styles.pagination}>
              {renderPagination}
              {!hasNext && <ShopNext data={getShopNextData(currentPage?.configuration)} />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CategoryContent;
